/**
 * Actycrea: youtube.esm.js (ES6)
 * MIT
 */

import { PREFIX } from '../helpers/config.js';
import { toMMSS } from '../helpers/helpers.js';
import { EVENT_PLAYING , EVENT_STOP } from '../helpers/events.js';

/* =========================
 * Constantes de configuración
 * =======================*/

const SELECTOR_DATA            = `[data-${PREFIX ?? 'wphack'}-myyt]`;
const SELECTOR_DATA_BUTTON     = `[data-${PREFIX ?? 'wphack'}-button]`;
const SELECTOR_DATA_STATE      = `[data-${PREFIX ?? 'wphack'}-state]`;
const SELECTOR_DATA_TIME       = `[data-${PREFIX ?? 'wphack'}-time]`;
const SELECTOR_DATA_TITLE      = `[data-${PREFIX ?? 'wphack'}-title]`;

const DEFAULTS = {
  id: false,
  videoId: false,
  dimensions: { height: 390, width: 640 },
  fullscreen: false
};

// Estado del reproductor “activo” de toda la página
let currentPlayerInstance = null;

// Carga única del API de YouTube
let ytApiPromise = null;
function loadYouTubeAPI( ) {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const first = document.getElementsByTagName('script')[0];
    first.parentNode.insertBefore(tag, first);
    window.onYouTubeIframeAPIReady = function () {
      resolve(window.YT);
    };
  });
  return ytApiPromise;
}



// Utilidades para utilizar en otros sitios
const PlayerUtils = {
  getCurrent: ( ) => currentPlayerInstance?.player ?? null,
  isPlaying: ( ) => {
    const p = PlayerUtils.getCurrent( );
    return Boolean( p && p.getPlayerState?.( ) === window.YT.PlayerState.PLAYING );
  },
  stopCurrent: () => {
    const p = PlayerUtils.getCurrent( );
    if (p) {
      p.stopVideo?.( );
      currentPlayerInstance = null;
    }
  }
};

export class MyYT {
  /**
   * @param {HTMLElement} element contenedor con data-myyt
   * @param {Object} config opcional
   */
  constructor(element, config = {}) {
    if (!(element instanceof HTMLElement)) throw new Error('MyYT: element inválido');

    // merge config: dataset -> config -> defaults
    const datasetCfg = {
      id:           element.dataset.id ?? false ,
      videoId:      element.dataset.videoId ?? false ,
      fullscreen:   element.dataset.fullscreen === 'true' || element.dataset.fullscreen === true || element.dataset.fullscreen === 1 || element.dataset.fullscreen === '1' ,
      dimensions:   {
        width:        element.dataset.width ? Number(element.dataset.width) : undefined,
        height:       element.dataset.height ? Number(element.dataset.height) : undefined
      }
    };

    this._element = element;
    this._config = {
      ...DEFAULTS,
      ...config,
      ...datasetCfg,
      dimensions: {
        ...DEFAULTS.dimensions,
        ...(config.dimensions || {}),
        ...(datasetCfg.dimensions || {})
      }
    };

    this._button  = element.querySelector( SELECTOR_DATA_BUTTON );
    this._stateEl = element.querySelector( SELECTOR_DATA_STATE );
    this._timeEl  = element.querySelector( SELECTOR_DATA_TIME );
    this._titleEl = element.querySelector( SELECTOR_DATA_TITLE );

    this._playing = false;
    this._tick = null; // setInterval id
    this.player = null;

    // bind
    this._onButtonClick = this._onButtonClick.bind(this);
    this._onPlayerReady = this._onPlayerReady.bind(this);
    this._onPlayerStateChange = this._onPlayerStateChange.bind(this);

    // init
    this.init();
  }

  async init() {
    if (!this._config.id || !this._config.videoId) throw new Error('MyYT: requiere "id" e "videoId"');

    if (this._button) this._button.addEventListener('click', this._onButtonClick);

    // cargar API y crear player
    await loadYouTubeAPI( );

    this.player = new window.YT.Player(this._config.id, {
      height: this._config.dimensions.height,
      width: this._config.dimensions.width,
      videoId: this._config.videoId,
      playerVars: {
        playsinline: 1,
        controls: 1,
        enablejsapi: 1,
        rel: 0,
        showinfo: 0,
        html5: 1,
        cc_load_policy: 0,
        wmode: 'transparent',
        autoplay: 0,
        disablekb: 1,
        loop: 0,
        modestbranding: 0,
        fs: this._config.fullscreen === false ? 1 : 0
      },
      events: {
        onReady: this._onPlayerReady,
        onStateChange: this._onPlayerStateChange
      }
    });
  }

  /* ============
   * Handlers
   * ===========*/
  _onPlayerReady(evt) {

    const iframe = this.player?.getIframe?.();
    if (iframe) {
      // Añade el atributo Microdata
      iframe.setAttribute( 'itemprop' , 'embedUrl' );
    }

    this._updateTitle( );           // título desde API o dataset
    this._refreshDuration( );       // fija duración cuando esté disponible
    this._setState( 'play' );       // estado inicial STOP
    this._renderTime( 0 , this._durationSec ?? 0 );

    console.log( 'MyYT: => _onPlayerReady( ) => evt , evt.target.getDuration ' , evt.target.getDuration( ) );

  }

  _onPlayerStateChange(evt) {
    const { PLAYING, ENDED, PAUSED, BUFFERING, CUED } = window.YT.PlayerState;
    if (evt.data === PLAYING) {
      this._playing = true;
      this._element.classList.add( 'playing' );
      this._setState( 'pause' );
      this._startTicker( );
      this._element.dispatchEvent(EVENT_PLAYING);
      if (this._config.fullscreen === true) this._setFullScreen(true);
    } else if ([ENDED, PAUSED, CUED].includes(evt.data)) {
      this._playing = false;
      this._element.classList.remove( 'playing' );
      this._setState( 'play' );
      this._stopTicker( );
      if ( evt.data !== PAUSED ) this._renderTime( 0 , this._durationSec ?? 0 );
      this._element.dispatchEvent(EVENT_STOP);
      if (this._config.fullscreen === true) this._setFullScreen(false);
    }
  }

  async _onButtonClick(e) {
    e.preventDefault();

    if (currentPlayerInstance && currentPlayerInstance !== this) PlayerUtils.stopCurrent();

    if (!this._playing) {
      currentPlayerInstance = this;
      this.player?.playVideo?.();
      if (this._button) this._button.setAttribute('aria-pressed' , 'true');
    } else {
      currentPlayerInstance = null;
      this.player?.pauseVideo?.();
      if (this._button) this._button.setAttribute('aria-pressed' , 'false');
    }
  }

  /* ============
   * API pública
   * ===========*/
  play() {
    if (currentPlayerInstance && currentPlayerInstance !== this) PlayerUtils.stopCurrent();
    currentPlayerInstance = this;
    this.player?.playVideo?.();
    console.log( 'MyYT: => play( )' );
  }

  pause() {
    this.player?.pauseVideo?.();
    if (currentPlayerInstance === this) currentPlayerInstance = null;
    console.log( 'MyYT: => pause( )' );
  }

  stop() {
    this.player?.stopVideo?.();
    if (currentPlayerInstance === this) currentPlayerInstance = null;
    console.log( 'MyYT: => stop( )' );
  }

  destroy() {
    try {
      this._stopTicker();
      this.pause();
      this.player?.destroy?.();
    } catch (_) { /* ignore */ }
    if (this._button) this._button.removeEventListener('click', this._onButtonClick);
    this.player = null;
    this._element = null;
  }

  /* ============
   * Helpers
   * ===========*/
  _updateTitle() {
    const apiTitle = this.player?.getVideoData?.()?.title;
    const dsTitle  = this._titleEl?.dataset?.[ `${PREFIX}Title` ] || this._titleEl?.dataset?.wphackTitle; // compat
    const title    = dsTitle || apiTitle || '';
    if (this._titleEl && title) this._titleEl.textContent = title;
  }

  _refreshDuration( tries = 0 ) {
    // La duración puede no estar lista en onReady; reintenta brevemente
    const dur = Number( this.player?.getDuration?.() || 0 );
    if (dur > 0) {
      this._durationSec = Math.floor( dur );
      this._renderTime( 0 , this._durationSec );
      return;
    }
    if (tries < 10) {
      setTimeout( () => this._refreshDuration( tries + 1 ) , 150 );
    } else {
      this._durationSec = 0;
      this._renderTime( 0 , 0 );
    }
  }

  _setState(kind) {
    if (!this._stateEl) return;
    // Espera datasets: data-play, data-pause, data-stop en el mismo nodo
    const map = {
      play:  this._stateEl.dataset.play  || 'PLAY',
      pause: this._stateEl.dataset.pause || 'PAUSE',
      stop:  this._stateEl.dataset.stop  || 'STOP'
    };
    const label = map[ kind ] || '';
    if (label) this._stateEl.textContent = label;
  }

  _renderTime(currentSec, totalSec) {
    if (!this._timeEl) return;
    const forceHours = Number(totalSec) >= 3600;
    const cur = toMMSS(currentSec, { forceHours });
    const tot = toMMSS(totalSec, { forceHours });
    this._timeEl.textContent = `${cur}/${tot}`;
  }

  _startTicker() {
    this._stopTicker( );
    this._tick = setInterval( () => {
      const cur = Number( this.player?.getCurrentTime?.() || 0 );
      const tot = Number( ( this._durationSec ?? this.player?.getDuration?.() ) || 0 );
      this._renderTime( cur , tot );
    } , 250 );
  }

  _stopTicker() {
    if ( this._tick ) {
      clearInterval( this._tick );
      this._tick = null;
    }
  }

  _setFullScreen(enable) {
    const iframe = this.player?.getIframe?.();
    if (!iframe) return;
    if (enable === true) {
      if (iframe.requestFullscreen) iframe.requestFullscreen();
      else if (iframe.webkitRequestFullscreen) iframe.webkitRequestFullscreen(); // Safari
      else if (iframe.mozRequestFullScreen) iframe.mozRequestFullScreen(); // Firefox
      else if (iframe.msRequestFullscreen) iframe.msRequestFullscreen(); // IE/Edge
    } else {
      if (document.fullscreenElement) document.exitFullscreen();
      else if (document.webkitFullscreenElement) document.webkitExitFullscreen();
      else if (document.mozFullScreenElement) document.mozCancelFullScreen();
      else if (document.msFullscreenElement) document.msExitFullscreen();
    }
  }

  /* ============
   * Init masiva
   * ===========*/
  static initAll(root = document) {
    const nodes = root.querySelectorAll( SELECTOR_DATA );
    const instances = [ ];
    nodes.forEach((el) => {
      // evita doble init guardando la instancia en dataset
      if (!el._myytInstance) el._myytInstance = new MyYT(el);
      instances.push(el._myytInstance);
    });
    return instances;
  }
}

// Utilidades exportadas
export const UtilsYT = PlayerUtils;
export async function ensureYouTubeAPI() { return loadYouTubeAPI(); }
export default MyYT;
